import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, type PrimitiveAtom } from 'jotai';
import { useHistory } from 'react-router-dom';
import { createColumnHelper, type Table } from '@tanstack/react-table';
import {
  ActionableNotification,
  Button,
  Checkbox,
  ToastNotification,
} from '@carbon/react';
import { MapBoundary } from '@carbon/react/icons';
import { Link } from 'components/link/link';
import {
  DataTable,
  ExportButton,
  type DataTableColumnDef,
} from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';
import { US_DATE_FORMAT, parseMomentUtc } from 'utils/dates';
import { AreasCatalogFilters } from './areas-catalog-filters';
import { AreasCatalogMap } from './areas-catalog-map';
import { DraggableDivider } from 'components/draggable-divider';
import type { MapWorkspaceState } from 'components/query-map/map-workspace-state';
import {
  createAreasCatalogSearchAtom,
  type AreaGeometriesQueryParams,
  type AreasQueryResponse,
} from './atoms';
import {
  columnFiltersToParams,
  DEFAULT_PARAMS,
  PAGE_SIZES,
} from './catalog-utils';
import { useAreasCatalogExport } from './hooks';
import styles from './areas-catalog-content.module.scss';

/** What DataTable renders for a cell with no value. */
const EMPTY_CELL = '—';

/** Floor for a dragged map, so it can be shrunk without disappearing. */
const MIN_MAP_PERCENT = 10;

/** A catalog row, as the query serializes it. */
type AreaRow = AreasQueryResponse & Record<string, unknown>;

const columnHelper = createColumnHelper<AreaRow>();

const yesNo = (value: boolean | undefined) => (value ? 'Yes' : 'No');

const formatDate = (value: string | undefined) => {
  return value ? parseMomentUtc(value).format(US_DATE_FORMAT) : EMPTY_CELL;
};

/**
 * Columns in the order the product spec lists them, less the two the query does
 * not serialize: nothing computes an area's validity and nothing writes a
 * `status`, so both were blank in every row — and the export drops them for the
 * same reason. Add them back with the work that populates them.
 */
export const columns = [
  columnHelper.accessor('displayName', {
    id: 'displayName',
    header: 'Area Name',
    size: 260,
    cell: (info) => (
      // The row marker lets the click handler resolve which area was clicked: the shared
      // table's `<tr>` carries no id and ignores row clicks in multi-select mode.
      <span data-area-id={info.row.original.id}>
        {/* The detail route is keyed on business_id so it always resolves to the
            area's current version (a bookmark never pins a superseded one); the row
            marker keeps the specific-version id for click selection. */}
        <Link to={`/area_geometries/${info.row.original.businessId}`}>
          {info.getValue()}
        </Link>
      </span>
    ),
  }),

  columnHelper.accessor('category', {
    id: 'category',
    header: 'Area Type',
    size: 160,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('shared', {
    id: 'shared',
    header: 'Shared',
    size: 100,
    meta: { type: 'boolean' },
    cell: (info) => yesNo(info.getValue()),
  }),

  columnHelper.accessor('active', {
    id: 'active',
    header: 'Active',
    size: 100,
    meta: { type: 'boolean' },
    cell: (info) => yesNo(info.getValue()),
  }),

  columnHelper.accessor('dataSource', {
    id: 'dataSource',
    header: 'Source',
    size: 160,
    meta: { type: 'enum' },
  }),

  columnHelper.accessor('createdBy', {
    id: 'createdBy',
    header: 'Created By',
    size: 160,
  }),

  // Who last changed the area; promoting an edit records the editor.
  columnHelper.accessor('updatedBy', {
    id: 'updatedBy',
    header: 'Updated By',
    size: 160,
  }),

  columnHelper.accessor('grossAcres', {
    id: 'grossAcres',
    header: 'Gross Acres',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      return value === null || value === undefined
        ? EMPTY_CELL
        : DECIMAL_FORMATTER_TWO_FRACTIONS.format(value);
    },
  }),

  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 140,
    meta: { type: 'date' },
    cell: (info) => formatDate(info.getValue()),
  }),

  columnHelper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Updated At',
    size: 140,
    meta: { type: 'date' },
    cell: (info) => formatDate(info.getValue()),
  }),
];

/**
 * Select-all header for the page's own checkbox column. The checked ids ride in
 * an atom rather than the column def, so the def stays stable — a def rebuilt
 * per click remounts the header and cells mid-interaction. The target of a
 * select-all is the page the table is showing, read from the table itself.
 *
 * @param {object} props - The table handle and the page's checked-ids atom.
 * @return {JSX.Element} The header checkbox.
 */
const AreasSelectAllHeader = ({
  table,
  checkedAtom,
}: {
  table: Table<AreaRow>;
  checkedAtom: PrimitiveAtom<string[]>;
}) => {
  const [checkedIds, setCheckedIds] = useAtom(checkedAtom);
  const rowIds = table.getRowModel().rows.map((row) => row.id);
  const checked = new Set(checkedIds);
  const allChecked = rowIds.length > 0 && rowIds.every((id) => checked.has(id));
  return (
    <Checkbox
      id="areas-catalog-select-all"
      labelText=""
      hideLabel
      checked={allChecked}
      indeterminate={checkedIds.length > 0 && !allChecked}
      disabled={rowIds.length === 0}
      onChange={(_evt, { checked: next }: { checked: boolean }) =>
        setCheckedIds(next ? rowIds : [])
      }
    />
  );
};

/**
 * One row's checkbox in the page's own selection column.
 *
 * @param {object} props - The row id and the page's checked-ids atom.
 * @return {JSX.Element} The row checkbox.
 */
const AreasSelectCell = ({
  rowId,
  checkedAtom,
}: {
  rowId: string;
  checkedAtom: PrimitiveAtom<string[]>;
}) => {
  const [checkedIds, setCheckedIds] = useAtom(checkedAtom);
  return (
    <Checkbox
      id={`areas-catalog-select-${rowId}`}
      labelText=""
      hideLabel
      checked={checkedIds.includes(rowId)}
      onChange={(_evt, { checked: next }: { checked: boolean }) => {
        setCheckedIds((current) => {
          return next
            ? [...current, rowId]
            : current.filter((id) => id !== rowId);
        });
      }}
    />
  );
};

export const initialTableState: InitialTableState = {
  // Active is ticked by default and DEFAULT_PARAMS sends `active: true` to match.
  // The filter state has to say so too, or the first touch of any filter rebuilds the
  // params from this state and silently un-hides the inactive areas.
  columnFilters: [{ id: 'active', value: ['true'] }],
  // `_select` is the page's own checkbox column. Pinning Area Name without it
  // puts the pinned name first and pushes the checkboxes to column two.
  columnPinning: { left: ['_select', 'displayName'] },
  sortColumn: 'displayName',
  sortDirection: 'asc',
  pageSize: 100,
};

interface AreasCatalogContentProps {
  /**
   * An area created by the panel. The catalog refreshes so the new row is in the
   * results, and offers a link to it — with the list sorted by name, a new area
   * usually lands on a page other than the one being looked at.
   */
  createdArea?: { id: number; name: string; businessId: string } | null;
  onCreatedAreaDismiss?: () => void;
  /**
   * Reports the checked rows so the page header's Create New Area button can
   * show the count and the panel can list the names.
   */
  onCheckedRowsChange?: (rows: { id: string; name: string }[]) => void;
  /**
   * Reports the current query params and filtered total to the page, so the
   * header's export buttons can scope and enable/disable themselves against the
   * exact set the table is showing.
   */
  onCatalogStateChange?: (state: {
    params: AreaGeometriesQueryParams;
    totalCount: number;
  }) => void;
  /**
   * Drawing state for the Create panel's user-drawn method, passed straight to
   * the map. Owned by the page because the panel is its sibling, not its child.
   */
  drawing: MapWorkspaceState;
}

/**
 * Areas catalog table: a server-driven `DataTable` over
 * `POST /queries/area_geometries`. Every table event funnels through one
 * `refetch` that rewrites the query params, matching the AFE Catalog page.
 *
 * @param {AreasCatalogContentProps} props - The component props.
 * @return {JSX.Element} The catalog table.
 */
const AreasCatalogContent: FC<AreasCatalogContentProps> = ({
  onCheckedRowsChange,
  onCatalogStateChange,
  drawing,
  createdArea = null,
  onCreatedAreaDismiss,
}) => {
  const history = useHistory();
  const { searchAtom } = useMemo(() => createAreasCatalogSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const [params, setParams] =
    useState<AreaGeometriesQueryParams>(DEFAULT_PARAMS);
  const [isMapOpen, setIsMapOpen] = useState(true);

  // The map's share of the shell once the divider has been dragged; null until
  // then, leaving it at its default (or workspace) height.
  const [mapPercent, setMapPercent] = useState<number | null>(null);
  const mapPaneRef = useRef<HTMLDivElement | null>(null);

  const handleMapResize = useCallback((percent: number) => {
    const pane = mapPaneRef.current;
    const shell = pane?.parentElement;
    if (!pane || !shell) {
      return;
    }

    // The divider reports the pointer as a percent of the whole shell, which
    // starts at the toolbar above the map — so the toolbar's share comes off,
    // or the map would jump that far down on the first drag.
    const offset =
      ((pane.getBoundingClientRect().top - shell.getBoundingClientRect().top) /
        shell.clientHeight) *
      100;
    setMapPercent(Math.max(MIN_MAP_PERCENT, percent - offset));
  }, []);

  // A hidden map is no use to a method that works on it, and the Create panel
  // has no way to know the map was collapsed — so needing it reveals it.
  const needsMap = drawing?.needsMap ?? false;
  useEffect(() => {
    if (needsMap) {
      setIsMapOpen(true);
    }
  }, [needsMap]);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  // The checked row ids, in an atom so the selection column's header and cells
  // (whose defs must stay stable) can subscribe to it directly.
  const checkedIdsAtom = useMemo(() => atom<string[]>([]), []);
  const checkedIds = useAtomValue(checkedIdsAtom);

  // Checked rows drive the map when there are any; otherwise the clicked row
  // does, on its own.
  const mappedIds = useMemo(() => {
    if (checkedIds.length > 0) {
      return checkedIds;
    }
    return highlightedId ? [highlightedId] : [];
  }, [checkedIds, highlightedId]);

  /**
   * Highlights the clicked row and points the map at it. Reads the id from the Area
   * Name cell's marker, since the shared table's `<tr>` carries none and ignores row
   * clicks in multi-select mode.
   *
   * @param {React.MouseEvent<HTMLDivElement>} event - The bubbled click.
   */
  const handleRowClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('input, label, a, button')) {
      return;
    }
    const marker = target.closest('tr')?.querySelector('[data-area-id]');
    const id = marker?.getAttribute('data-area-id');
    if (id) {
      setHighlightedId(id);
    }
  }, []);

  // Params → atom sync: triggers the fetch whenever params change.
  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  // Re-issues the current query. A fresh object is what the atom reacts to, so
  // the same params still refetch — needed after a row changes underneath us.
  const refreshList = useCallback(() => {
    setSearchPayload(() => ({ ...params }));
  }, [params, setSearchPayload]);

  // A new area won't be in the results the table already has, so re-run the
  // query when one arrives. Keyed on the id so a second create refreshes again.
  const createdAreaId = createdArea?.id ?? null;
  useEffect(() => {
    if (createdAreaId !== null) {
      refreshList();
    }
  }, [createdAreaId, refreshList]);

  /**
   * Single funnel for every table-driven param change, so pagination, sorting,
   * search and filters can't drift apart.
   *
   * @param {TableState} state - Post-change table state snapshot.
   */
  const refetch = useCallback(
    ({ page, pageSize, globalFilter, columnFilters }: TableState) => {
      setParams({
        ...DEFAULT_PARAMS,
        page,
        pageSize,
        text: globalFilter || undefined,
        ...columnFiltersToParams(columnFilters),
      });
    },
    []
  );

  const rows = useMemo(
    () => (searchResult.data?.results ?? []) as AreaRow[],
    [searchResult.data?.results]
  );

  // The page owns its checkbox column rather than using the table's
  // `selectionType: 'multi'`: the shared select-all header loses its own flag
  // (its second dispatch carries the render's stale value), which left every
  // checkbox unticked while the page counted the rows as selected. Selection
  // lives in the page's atom instead, and select-all is scoped to the loaded
  // page, as the product spec asks. The `_select` id keeps the reserved
  // treatment — pinned left, fixed width — and its meta keeps it out of the
  // column menus and export.
  const allColumns = useMemo((): DataTableColumnDef<AreaRow>[] => {
    const selectColumn: DataTableColumnDef<AreaRow> = {
      id: '_select',
      header: ({ table }) => (
        <AreasSelectAllHeader table={table} checkedAtom={checkedIdsAtom} />
      ),
      cell: ({ row }) => (
        <AreasSelectCell rowId={row.id} checkedAtom={checkedIdsAtom} />
      ),
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      meta: {
        toggleColumnFilter: false,
        enablePinning: false,
        enableReordering: false,
        isExportable: false,
      },
    };
    return [selectColumn, ...(columns as DataTableColumnDef<AreaRow>[])];
  }, [checkedIdsAtom]);

  // Report the checked rows upward. Names come from the current page, so a row
  // checked on an earlier page contributes its id without a name.
  useEffect(() => {
    onCheckedRowsChange?.(
      checkedIds.map((id) => ({
        id,
        name:
          rows.find((row) => String(row.id) === id)?.displayName ??
          `Area ${id}`,
      }))
    );
  }, [checkedIds, rows, onCheckedRowsChange]);

  // Report the current query params + filtered total up to the page, so the
  // header spatial-export button scopes to exactly what the table shows.
  const totalCount = searchResult.data?.totalCount ?? 0;
  useEffect(() => {
    onCatalogStateChange?.({ params, totalCount });
  }, [params, totalCount, onCatalogStateChange]);

  // The catalog (Excel) export is the toolbar download icon (matching prod): it runs server-side off
  // the current filters and the finished file arrives over Pusher. A single preset, so the toolbar
  // shows one icon rather than a menu.
  const { exportStatus, startExport, resetExport, userId } =
    useAreasCatalogExport();

  const handleCatalogExport = useCallback(() => {
    // The completion channel is named after the signed-in user, so without a session there is
    // nothing to listen on.
    if (!userId) return;
    void startExport({ queryParams: params });
  }, [userId, startExport, params]);

  const exportPresets = useMemo(
    () => [
      {
        name: 'Export',
        format: 'xlsx' as const,
        onExport: handleCatalogExport,
        // The completion channel is named after the signed-in user, so without a session the export has
        // nowhere to report back — disable it with a reason rather than letting a click silently no-op.
        disabledReason: userId
          ? undefined
          : 'Cannot start export: user session unavailable.',
      },
    ],
    [handleCatalogExport, userId]
  );

  return (
    // Click capture for row highlighting — see handleRowClick. Keyboard users
    // reach the same area through the Area Name link in each row.
    <div className={styles.tableWrapper} onClick={handleRowClick}>
      <DataTable<AreaRow>
        data={rows}
        columns={allColumns}
        getRowId={(row, index) => String(row.id ?? index)}
        initialTableState={initialTableState}
        persistenceKey="areas-catalog-list-table"
        config={{
          columnFilters: false,
        }}
        isLoading={searchResult.loading}
        error={searchResult.error ?? null}
        pageSizes={PAGE_SIZES}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: searchResult.data?.totalCount ?? 0,
        }}
        searchPlaceholder="Search areas"
        onPaginationChange={refetch}
        onSortChange={refetch}
        onFilterChange={refetch}
        onGlobalSearchChange={refetch}
        highlightedRowIds={highlightedId ? new Set([highlightedId]) : undefined}
        leftToolbarActions={[
          <Button
            key="map-toggle"
            kind="ghost"
            size="md"
            hasIconOnly
            iconDescription={isMapOpen ? 'Hide map' : 'Show map'}
            renderIcon={MapBoundary}
            onClick={() => setIsMapOpen((open) => !open)}
          />,
        ]}
        // The catalog (Excel) export entry point sits at the far right, as prod has it: the toolbar
        // download icon, scoped to the current catalog filters and delivered over Pusher.
        rightToolbarActions={[
          <ExportButton key="export" config={{ exportPresets }} />,
        ]}
        betweenToolbarAndContent={
          isMapOpen ? (
            <>
              <AreasCatalogMap
                // While a method works on the map, the checked rows belong to a
                // different method and only clutter the canvas being drawn on — so
                // the map shows just what is being built.
                ids={needsMap ? [] : mappedIds}
                drawnPolygons={drawing?.polygons}
                onDrawnPolygonsChange={drawing?.onPolygonsChange}
                drawing={drawing?.isDrawing}
                onDrawingChange={drawing?.onIsDrawingChange}
                editingIndex={drawing?.editingIndex}
                onEditingIndexChange={drawing?.onEditingIndexChange}
                previewGeometry={drawing?.previewGeometry ?? null}
                previewColor={drawing?.previewColor ?? null}
                onUnitClick={drawing?.onUnitClick ?? null}
                onSurveyAreaPick={drawing?.onSurveyAreaPick ?? null}
                pickedSurveyAreas={drawing?.pickedSurveyAreas}
                snapToGrid={drawing?.snapToGrid}
                tall={needsMap}
                heightPercent={mapPercent}
                paneRef={mapPaneRef}
              />
              {/* Drag to trade height between the map and the table (UAT 12). */}
              <DraggableDivider onDrag={handleMapResize} />
            </>
          ) : undefined
        }
        renderFilterPanel={() => (
          <AreasCatalogFilters facets={searchResult.data?.facets} />
        )}
      />

      {/*
        Archiving drops the row straight out of an active-only list, so the
        confirmation carries the way back rather than leaving the user to find
        the area again behind a filter change.
      */}
      {/*
        Created areas announce themselves here rather than by navigating: the
        panel closes, the list refreshes underneath, and this offers the way to
        the new area — which is worth offering, because sorted by name it rarely
        appears on the page being looked at.
      */}
      {createdArea && (
        <div className={styles.toast}>
          <ActionableNotification
            kind="success"
            lowContrast
            // Announce, don't interrupt. The default role is `alertdialog`, which
            // focuses the action button on mount and then wraps focus back inside on
            // every blur — so until this was dismissed you could not click into the
            // search box or anything else on the page.
            role="status"
            hasFocus={false}
            title={`'${createdArea.name}' created.`}
            actionButtonLabel="View area"
            onActionButtonClick={() => {
              // Navigate by business_id, not the version id: the detail route resolves a
              // business_id to the current promoted version, so create -> view -> promote
              // keeps following the latest version instead of pinning to this freshly-created
              // one (which otherwise reads a stale "Shared: No" once a promote publishes a new
              // version). A just-created area can briefly 404 through the ES-backed resolver
              // until it indexes — accepted for now; follow-up: fall back to a PG lookup in
              // use-current-area if that lag ever actually surfaces.
              history.push(`/area_geometries/${createdArea.businessId}`);
              onCreatedAreaDismiss?.();
            }}
            onClose={() => {
              onCreatedAreaDismiss?.();
              return true;
            }}
          />
        </div>
      )}

      {/*
        The catalog export runs in the background, so a start failure has no toolbar button left to
        report against by the time it happens — surface it here so it isn't silent.
      */}
      {exportStatus.status === 'failed' && (
        <div className={styles.toast}>
          <ToastNotification
            kind="error"
            lowContrast
            title="Export failed"
            subtitle={
              exportStatus.error ?? 'Failed to start export. Please try again.'
            }
            onClose={() => {
              resetExport();
              return true;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AreasCatalogContent;
